import React, { useEffect, useRef, useState } from 'react';

const queryOptions = ['--Select--', 'Open', 'Accepted'];
const statusOptions = [
  '--Select--',
  'ALL',
  'Initiated',
  'Closed',
  'Completed',
  'Refer',
  'Approved',
  'Corrected',
  'Advised',
];

const BackofficeQueriesPanel = ({
  isActive,
  form,
  onFormUpdate,
  selectedAgentName,
  onSelectAgent,
  agentOptions,
  agentsErr,
  mainCats,
  mainCatsErr,
  isViewOnly = false,
  onCloseGI,
  onShow,
  loading,
  err,
  rows,
  resultsLabel,
  activeQid,
  onRowDoubleClick,
  getRowKey,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
}) => {
  const updateField = (e) => {
    onFormUpdate?.({ [e.target.name]: e.target.value });
  };

  const selectedAgents = Array.isArray(selectedAgentName) ? selectedAgentName : [];
  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  const agentDropdownRef = useRef(null);

  useEffect(() => {
    if (!agentDropdownOpen) return undefined;
    const handleOutside = (event) => {
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(event.target)) {
        setAgentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [agentDropdownOpen]);

  const toggleAgent = (name) => {
    if (!name) return;
    if (selectedAgents.includes(name)) {
      onSelectAgent?.(selectedAgents.filter((n) => n !== name));
    } else {
      onSelectAgent?.([...selectedAgents, name]);
    }
  };

  const selectedAgentsLabel =
    selectedAgents.length === 0
      ? '--Select--'
      : selectedAgents.length <= 2
      ? selectedAgents.join(', ')
      : `${selectedAgents.length} selected`;

  if (!isActive) return null;

  return (
    <section className="module-panel active" id="module-queries">
      <section className="panel legacy-top reveal is-visible">
        <div className="panel-head">
          <h3>Queries Desk</h3>
          <span className="status-chip pending">Live Queue</span>
        </div>

        <div className="queries-grid">
          <aside className="query-filters">
            <label>
              <span>Queries</span>
              <select name="query" value={form.query || ''} onChange={updateField}>
                {queryOptions.map((option) => {
                  const value = option === '--Select--' ? '' : option;
                  return (
                    <option key={option} value={value}>
                      {option}
                    </option>
                  );
                })}
              </select>
            </label>
            <label>
              <span>Status</span>
              <select name="status" value={form.status || ''} onChange={updateField}>
                {statusOptions.map((option) => {
                  const value = option === '--Select--' ? '' : option;
                  return (
                    <option key={option} value={value}>
                      {option}
                    </option>
                  );
                })}
              </select>
            </label>
            <label>
              <span>Query ID</span>
              <input name="queryId" value={form.queryId || ''} onChange={updateField} />
            </label>
            <label>
              <span>Agents</span>
              <div className="dropdown" ref={agentDropdownRef}>
                <button
                  type="button"
                  className="form-select text-start d-flex justify-content-between align-items-center"
                  onClick={() => setAgentDropdownOpen((open) => !open)}
                  aria-expanded={agentDropdownOpen}
                >
                  <span>{selectedAgentsLabel}</span>
                  <span className="ms-2">&#9662;</span>
                </button>
                {agentDropdownOpen && (
                  <div
                    className="dropdown-menu show p-2"
                    style={{ maxHeight: 220, overflowY: 'auto', width: '100%' }}
                  >
                    <label className="d-flex align-items-center gap-2 mb-1 small" style={{ padding: '2px 4px' }}>
                      <input
                        type="checkbox"
                        className="form-check-input m-0"
                        style={{ width: '0.9rem', height: '0.9rem' }}
                        checked={selectedAgents.length === 0}
                        onChange={() => onSelectAgent?.([])}
                      />
                      <span>--Select--</span>
                    </label>
                    {(agentOptions || []).length === 0 && (
                      <div className="text-muted small px-2">No agents</div>
                    )}
                    {(agentOptions || []).map((agent) => {
                      const name = agent?.name ?? agent?.id ?? '';
                      if (!name) return null;
                      const checked = selectedAgents.includes(name);
                      return (
                        <label key={name} className="d-flex align-items-center gap-2 mb-1 small" style={{ padding: '2px 4px' }}>
                          <input
                            type="checkbox"
                            className="form-check-input m-0"
                            style={{ width: '0.9rem', height: '0.9rem' }}
                            checked={checked}
                            onChange={() => toggleAgent(name)}
                          />
                          <span>{name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </label>
            <label>
              <span>Date From</span>
              <input type="date" name="date" value={form.date || ''} onChange={updateField} />
            </label>
            <label>
              <span>Date To</span>
              <input type="date" name="dateTo" value={form.dateTo || ''} onChange={updateField} />
            </label>
            <label>
              <span>Main Category</span>
              <select name="mainCat" value={form.mainCat || ''} onChange={updateField}>
                <option value="">--Select--</option>
                {Array.isArray(mainCats) && mainCats.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </label>

            <div className="filter-actions-row">
              {!isViewOnly && (
                <button className="btn btn-soft" type="button" onClick={onCloseGI}>
                  Close GI
                </button>
              )}
              <button className="btn btn-primary" type="button" onClick={onShow} disabled={loading}>
                {loading ? 'Loading…' : 'Show'}
              </button>
            </div>

            {agentsErr && <div className="alert alert-danger py-2 mt-2">{agentsErr}</div>}
            {mainCatsErr && <div className="alert alert-danger py-2 mt-2">{mainCatsErr}</div>}
          </aside>

          <section className="query-results">
            <div className="results-head">
              <h4>Open Queries</h4>
              <span>{resultsLabel}</span>
            </div>
            {err && <div className="alert alert-danger py-2 m-2">{err}</div>}
            <div className="results-table-wrap">
              <table className="queries-wide-table">
                <thead>
                  <tr>
                    <th>QID</th>
                    <th>CallersID</th>
                    <th>ContactPerson</th>
                    <th>Status</th>
                    <th>ContactNumber</th>
                    <th>RoutedToDepartment</th>
                    <th>Edited</th>
                    <th>MainCat</th>
                    <th>Cat</th>
                    <th>Sub-Cat</th>
                    <th>Office</th>
                    <th>CreatedOn</th>
                    <th>ModifiedOn</th>
                    <th>ClosedOn</th>
                    <th>History</th>
                    <th>Notes</th>
                    <th>TAGS</th>
                    <th>CreatedBy</th>
                    <th>ModifiedBy</th>
                    <th>RoutedOn</th>
                    <th>RouteBy</th>
                  </tr>
                </thead>
                <tbody>
                  {(rows || []).map((row) => {
                    const key = getRowKey?.(row) || row.queryId || row.QueryID || '';
                    return (
                <tr
                  key={key}
                  className={key && key === activeQid ? 'is-selected' : ''}
                  onClick={() => onRowDoubleClick?.(row)}
                >
                        <td>{row.queryId || row.QueryID || key}</td>
                        <td>{row.callerId || row.CallersID || ''}</td>
                        <td>{row.contactPerson || row.ContactPerson || ''}</td>
                        <td>{row.status || row.Status || ''}</td>
                        <td>{row.contactNumber || row.ContactNumber || ''}</td>
                        <td>{row.routedToDepartment || row.RoutedToDepartment || ''}</td>
                        <td>{row.edited || row.Edited || ''}</td>
                        <td>{row.mainCat || row.MainCat || ''}</td>
                        <td>{row.cat || row.Cat || ''}</td>
                        <td>{row.subCat || row['Sub-Cat'] || ''}</td>
                        <td>{row.office || row.Office || ''}</td>
                        <td>{row.createdOn || row.CreatedOn || ''}</td>
                        <td>{row.modifiedOn || row.ModifiedOn || ''}</td>
                        <td>{row.closedOn || row.ClosedOn || ''}</td>
                        <td>{row.history || row.History || ''}</td>
                        <td>{row.notes || row.Notes || ''}</td>
                        <td>{row.tags || row.TAGS || ''}</td>
                        <td>{row.createdBy || row.CreatedBy || ''}</td>
                        <td>{row.modifiedBy || row.ModifiedBy || ''}</td>
                        <td>{row.routedOn || row.RoutedOn || ''}</td>
                        <td>{row.routedBy || row.RouteBy || row.RoutedBy || ''}</td>
                      </tr>
                    );
                  })}
                  {!rows?.length && (
                    <tr>
                      <td colSpan={21} className="query-history-empty">No results.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="results-pager">
              <button
                className="btn btn-soft"
                type="button"
                onClick={() => onPageChange?.(currentPage - 1)}
                disabled={currentPage <= 1 || loading}
              >&lt;&lt;</button>
              <span style={{ alignSelf: 'center', fontSize: '0.85rem', padding: '0 8px' }}>
                {currentPage} / {totalPages}
              </span>
              <button
                className="btn btn-soft"
                type="button"
                onClick={() => onPageChange?.(currentPage + 1)}
                disabled={currentPage >= totalPages || loading}
              >&gt;&gt;</button>
            </div>
          </section>
        </div>
      </section>
    </section>
  );
};

export default BackofficeQueriesPanel;
